import { useCallback, useEffect, useRef, useState } from 'react'
import { decodeMorse } from '../core/morse'
import {
  DEFAULT_KEY_BINDINGS,
  characterGapMs,
  createKeyerState,
  elementDurationMs,
  inputForKeyboardCode,
  transitionKeyer,
  wpmToUnitMs,
  type HeldInputs,
  type KeyBindings as CoreKeyBindings,
  type KeyerMode,
  type KeyerState,
  type KeyInput,
} from '../core/keyer'

export type InputMode = KeyerMode
export type KeyBindings = CoreKeyBindings
export type MorseInput = KeyInput

export interface UseMorseInputOptions {
  mode: InputMode
  wpm: number
  frequency?: number
  volume?: number
  keyBindings?: Partial<KeyBindings>
  enabled?: boolean
  onCharacter?: (character: string, sequence: string) => void
}

export interface MorseInputControls {
  /** The dot/dash pattern currently being entered. */
  sequence: string
  /** Alias useful for views that prefer the more explicit name. */
  currentSequence: string
  active: HeldInputs
  isDitPressed: boolean
  isDahPressed: boolean
  isStraightPressed: boolean
  /** Shared entry point for pointer, USB HID, BLE HID, and custom controls. */
  press: (input: MorseInput, atMs?: number) => void
  release: (input: MorseInput, atMs?: number) => void
  reset: () => void
}

interface PlayingTone {
  oscillator: OscillatorNode
  gain: GainNode
}

type Timer = ReturnType<typeof setTimeout>

function clockNow(): number {
  return typeof performance === 'undefined' ? Date.now() : performance.now()
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (typeof HTMLElement === 'undefined' || !(target instanceof HTMLElement)) {
    return false
  }

  return (
    target.isContentEditable ||
    target.tagName === 'INPUT' ||
    target.tagName === 'TEXTAREA' ||
    target.tagName === 'SELECT'
  )
}

/**
 * Converts keyboard/HID and pointer input into timed Morse elements. Keyboard
 * HID devices work automatically when they expose normal KeyboardEvent codes.
 */
export function useMorseInput({
  mode,
  wpm,
  frequency = 600,
  volume = 0.35,
  keyBindings,
  enabled = true,
  onCharacter,
}: UseMorseInputOptions): MorseInputControls {
  const keyerRef = useRef<KeyerState>(createKeyerState(mode))
  const [view, setView] = useState<KeyerState>(keyerRef.current)

  const modeRef = useRef(mode)
  const wpmRef = useRef(wpm)
  const frequencyRef = useRef(frequency)
  const volumeRef = useRef(volume)
  const bindingsRef = useRef<KeyBindings>({ ...DEFAULT_KEY_BINDINGS })
  const onCharacterRef = useRef(onCharacter)
  const enabledRef = useRef(enabled)
  const mountedRef = useRef(false)

  modeRef.current = mode
  wpmRef.current = wpm
  frequencyRef.current = frequency
  volumeRef.current = volume
  bindingsRef.current = { ...DEFAULT_KEY_BINDINGS, ...keyBindings }
  onCharacterRef.current = onCharacter
  enabledRef.current = enabled

  const audioContextRef = useRef<AudioContext | null>(null)
  const playingToneRef = useRef<PlayingTone | null>(null)
  const paddleRunningRef = useRef(false)
  const paddleTimerRef = useRef<Timer | null>(null)
  const toneTimerRef = useRef<Timer | null>(null)
  const characterTimerRef = useRef<Timer | null>(null)
  const runPaddleCycleRef = useRef<() => void>(() => undefined)

  const publish = useCallback((state: KeyerState) => {
    keyerRef.current = state
    if (mountedRef.current) setView(state)
  }, [])

  const stopTone = useCallback(() => {
    const playing = playingToneRef.current
    if (!playing) return
    playingToneRef.current = null

    const context = audioContextRef.current
    try {
      const now = context?.currentTime ?? 0
      playing.gain.gain.cancelScheduledValues(now)
      playing.gain.gain.setValueAtTime(playing.gain.gain.value, now)
      playing.gain.gain.linearRampToValueAtTime(0, now + 0.008)
      playing.oscillator.stop(now + 0.01)
    } catch {
      // An oscillator may already have stopped while the page was suspended.
    }
  }, [])

  const startTone = useCallback(() => {
    if (typeof window === 'undefined' || volumeRef.current <= 0) return

    try {
      if (!audioContextRef.current) {
        const AudioContextConstructor =
          window.AudioContext ??
          (window as typeof window & {
            webkitAudioContext?: typeof AudioContext
          }).webkitAudioContext
        if (!AudioContextConstructor) return
        audioContextRef.current = new AudioContextConstructor()
      }

      const context = audioContextRef.current
      if (context.state === 'suspended') void context.resume().catch(() => undefined)

      stopTone()
      const oscillator = context.createOscillator()
      const gain = context.createGain()
      const now = context.currentTime
      const targetVolume = Math.min(1, Math.max(0, volumeRef.current))

      oscillator.type = 'sine'
      oscillator.frequency.setValueAtTime(
        Math.max(1, frequencyRef.current),
        now,
      )
      gain.gain.setValueAtTime(0, now)
      gain.gain.linearRampToValueAtTime(targetVolume, now + 0.005)
      oscillator.connect(gain)
      gain.connect(context.destination)
      oscillator.onended = () => {
        oscillator.disconnect()
        gain.disconnect()
      }
      oscillator.start(now)
      playingToneRef.current = { oscillator, gain }
    } catch {
      // Audio may be blocked by browser policy; keying must remain functional.
    }
  }, [stopTone])

  const clearCharacterTimer = useCallback(() => {
    if (characterTimerRef.current !== null) {
      clearTimeout(characterTimerRef.current)
      characterTimerRef.current = null
    }
  }, [])

  const commitCharacter = useCallback(() => {
    characterTimerRef.current = null
    if (!mountedRef.current) return

    const unitMs = wpmToUnitMs(wpmRef.current)
    const transition = transitionKeyer(
      keyerRef.current,
      { type: 'commit' },
      unitMs,
    )
    if (!transition.completedSequence) return

    publish(transition.state)
    const character = decodeMorse(transition.completedSequence) ?? '�'
    onCharacterRef.current?.(character, transition.completedSequence)
  }, [publish])

  const scheduleCharacterCommit = useCallback(
    (afterSignalMs: number) => {
      clearCharacterTimer()
      const delay = afterSignalMs + characterGapMs(wpmToUnitMs(wpmRef.current))
      characterTimerRef.current = setTimeout(commitCharacter, delay)
    },
    [clearCharacterTimer, commitCharacter],
  )

  const runPaddleCycle = useCallback(() => {
    paddleTimerRef.current = null
    if (!mountedRef.current || modeRef.current !== 'paddle') {
      paddleRunningRef.current = false
      return
    }

    const unitMs = wpmToUnitMs(wpmRef.current)
    const transition = transitionKeyer(
      keyerRef.current,
      { type: 'paddleTick' },
      unitMs,
    )
    if (!transition.element) {
      paddleRunningRef.current = false
      return
    }

    paddleRunningRef.current = true
    publish(transition.state)
    const signalMs = elementDurationMs(transition.element, unitMs)
    scheduleCharacterCommit(signalMs)
    startTone()

    if (toneTimerRef.current !== null) clearTimeout(toneTimerRef.current)
    toneTimerRef.current = setTimeout(() => {
      toneTimerRef.current = null
      stopTone()
    }, signalMs)
    paddleTimerRef.current = setTimeout(
      () => runPaddleCycleRef.current(),
      signalMs + unitMs,
    )
  }, [publish, scheduleCharacterCommit, startTone, stopTone])

  runPaddleCycleRef.current = runPaddleCycle

  const press = useCallback(
    (input: MorseInput, atMs = clockNow()) => {
      if (!enabledRef.current) return

      const unitMs = wpmToUnitMs(wpmRef.current)
      const transition = transitionKeyer(
        keyerRef.current,
        { type: 'press', input, atMs },
        unitMs,
      )
      if (transition.state === keyerRef.current) return

      publish(transition.state)
      if (input === 'straight') {
        // Silence is measured up to the next closure, not its eventual release.
        clearCharacterTimer()
        startTone()
      } else if (!paddleRunningRef.current) {
        paddleRunningRef.current = true
        runPaddleCycleRef.current()
      }
    },
    [clearCharacterTimer, publish, startTone],
  )

  const release = useCallback(
    (input: MorseInput, atMs = clockNow()) => {
      const unitMs = wpmToUnitMs(wpmRef.current)
      const transition = transitionKeyer(
        keyerRef.current,
        { type: 'release', input, atMs },
        unitMs,
      )
      if (transition.state === keyerRef.current) return

      publish(transition.state)
      if (input === 'straight') {
        stopTone()
        if (transition.element) scheduleCharacterCommit(0)
      }
    },
    [publish, scheduleCharacterCommit, stopTone],
  )

  const reset = useCallback(() => {
    clearCharacterTimer()
    if (paddleTimerRef.current !== null) {
      clearTimeout(paddleTimerRef.current)
      paddleTimerRef.current = null
    }
    if (toneTimerRef.current !== null) {
      clearTimeout(toneTimerRef.current)
      toneTimerRef.current = null
    }
    paddleRunningRef.current = false
    stopTone()
    publish(createKeyerState(modeRef.current))
  }, [clearCharacterTimer, publish, stopTone])

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      clearCharacterTimer()
      if (paddleTimerRef.current !== null) clearTimeout(paddleTimerRef.current)
      if (toneTimerRef.current !== null) clearTimeout(toneTimerRef.current)
      paddleTimerRef.current = null
      toneTimerRef.current = null
      paddleRunningRef.current = false
      stopTone()

      const context = audioContextRef.current
      audioContextRef.current = null
      if (context && context.state !== 'closed') {
        void context.close().catch(() => undefined)
      }
    }
  }, [clearCharacterTimer, stopTone])

  useEffect(() => {
    reset()
  }, [mode, reset])

  useEffect(() => {
    if (!enabled) reset()
  }, [enabled, reset])

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return

    const activeCodes = new Map<KeyboardEvent['code'], MorseInput>()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.repeat || event.isComposing || activeCodes.has(event.code)) return
      if (isEditableTarget(event.target)) return

      const input = inputForKeyboardCode(
        modeRef.current,
        event.code,
        bindingsRef.current,
      )
      if (!input) return

      event.preventDefault()
      activeCodes.set(event.code, input)
      press(input, event.timeStamp)
    }

    const onKeyUp = (event: KeyboardEvent) => {
      const input = activeCodes.get(event.code)
      if (!input) return

      event.preventDefault()
      activeCodes.delete(event.code)
      release(input, event.timeStamp)
    }

    const onBlur = () => {
      activeCodes.clear()
      reset()
    }

    window.addEventListener('keydown', onKeyDown)
    window.addEventListener('keyup', onKeyUp)
    window.addEventListener('blur', onBlur)
    return () => {
      window.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('keyup', onKeyUp)
      window.removeEventListener('blur', onBlur)
      activeCodes.clear()
    }
  }, [enabled, press, release, reset])

  return {
    sequence: view.sequence,
    currentSequence: view.sequence,
    active: view.held,
    isDitPressed: view.held.dit,
    isDahPressed: view.held.dah,
    isStraightPressed: view.held.straight,
    press,
    release,
    reset,
  }
}
